import React, { useEffect, useMemo } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Download, Loader2, MessageCircle, ArrowRight } from 'lucide-react';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import AppDownloadButton from '../components/AppDownloadButton';
import { useAppDownload } from '../hooks/useAppDownload';
import { RECRUITER_WHATSAPP_LINK, WORKER_WHATSAPP_LINK } from '../constants/externalLinks';

type DownloadTarget = 'worker' | 'recruiter';

export default function AppDownloadRedirectPage() {
  const params = useParams<{ target: string }>();
  const target = (params.target || '').toLowerCase() as DownloadTarget;

  const isValidTarget = target === 'worker' || target === 'recruiter';
  const { downloadUrl, loading } = useAppDownload(isValidTarget ? target : 'worker');

  useEffect(() => {
    if (!downloadUrl) return;
    window.location.replace(downloadUrl);
  }, [downloadUrl]);

  const copy = useMemo(() => {
    if (target === 'recruiter') {
      return {
        title: 'Recruiter App Download',
        body: 'This link always redirects to the latest recruiter app build configured in the admin panel.',
        whatsappUrl: RECRUITER_WHATSAPP_LINK,
        secondaryHref: '/login',
        secondaryLabel: 'Open Recruiter Portal',
      };
    }

    return {
      title: 'Worker App Download',
      body: 'This link always redirects to the latest worker app build configured in the admin panel.',
      whatsappUrl: WORKER_WHATSAPP_LINK,
      secondaryHref: '/worker/login',
      secondaryLabel: 'Open Worker Web Portal',
    };
  }, [target]);

  if (!isValidTarget) {
    return <Navigate to="/download" replace />;
  }

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#fffaf5_0%,#ffffff_45%,#f8fafc_100%)] text-slate-900">
      <Seo
        title={`${copy.title} | Jobisa`}
        description={copy.body}
        path={`/download/${target}`}
      />

      <main className="mx-auto flex min-h-[calc(100vh-120px)] max-w-3xl items-center px-6 py-16 lg:px-10">
        <section className="w-full rounded-[2rem] border border-orange-100 bg-white p-8 shadow-[0_24px_60px_-32px_rgba(15,23,42,0.22)] lg:p-10">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-orange-600">Stable Download Link</p>
          <h1 className="mt-4 text-4xl font-black tracking-tight text-slate-950">{copy.title}</h1>
          <p className="mt-4 text-lg leading-8 text-slate-600">{copy.body}</p>

          <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-5">
            <div className="flex items-center gap-3 text-slate-700">
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
              <span className="font-semibold">
                {loading ? 'Checking latest version from Jobisa admin...' : downloadUrl ? 'Redirecting to the latest build...' : 'No active download link found yet.'}
              </span>
            </div>
          </div>

          <div className="mt-8 flex flex-col gap-4 sm:flex-row">
            <AppDownloadButton
              href={downloadUrl}
              openInNewTab={true}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-950 px-6 py-3.5 text-sm font-semibold text-white transition hover:bg-slate-800"
            >
              <Download size={17} />
              Open Latest Download
            </AppDownloadButton>
            <a
              href={copy.whatsappUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-6 py-3.5 text-sm font-semibold text-emerald-700 transition hover:border-emerald-300 hover:bg-emerald-100"
            >
              <MessageCircle size={17} />
              Chat on WhatsApp
            </a>
            <a
              href={copy.secondaryHref}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-6 py-3.5 text-sm font-semibold text-orange-700 transition hover:border-orange-300 hover:bg-orange-100"
            >
              <ArrowRight size={17} />
              {copy.secondaryLabel}
            </a>
          </div>

          <p className="mt-6 text-sm text-slate-500">
            Use this exact link in QR codes and posters: <span className="font-mono text-slate-700">{`jobisa.in/download/${target}`}</span>
          </p>
        </section>
      </main>

      <Footer />
    </div>
  );
}
